import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { ReactNode, useState } from 'react';
import {
  FormInput, UpdateType, useModelForm,
} from '../modelInputs';
import { Input } from './Input';
import { Flex } from '../../Flex';
import { RadioInput, ThemeType, useFormConfigurationContext } from '../../../..';

export const RadioListInputWrapper = styled(Flex)<{css?: FlattenInterpolation<ThemeProps<ThemeType>> | string}>`
  width: max-content;
  ${(props) => props.css}
`;

type RadioListInputProps = {
  id?: string,
  field: string,
  label?: ReactNode,
  options: { [key: string]: string },
  labelOverride?: { [key: string]: string },
  required?: boolean;
  touchOverride?: boolean,
  errorOverride?: string,
  updateOverride?: UpdateType,
  valueOverride?: string,
  isInline?: boolean,
  disabled?: boolean | Record<string, boolean>,
  containerStyle?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  tooltip?: ReactNode,
  hasError?: boolean,
}

export function RadioListInput({
  id,
  field,
  label,
  options = {},
  labelOverride = {},
  touchOverride = false,
  errorOverride = '',
  updateOverride,
  valueOverride,
  required,
  isInline,
  disabled = false,
  containerStyle = '',
  tooltip = '',
  hasError = false,
}: RadioListInputProps) {
  const {
    visible, errors, value, update, warnings,
  } = useModelForm(field, valueOverride, errorOverride, updateOverride);
  const [touched, setTouched] = useState<boolean>(false);
  const showError = (hasError || !!errors.length) && touched;

  const { isDisabled } = useFormConfigurationContext();

  const localUpdate = (key: string, newValue: boolean) => {
    if (newValue) {
      update({ [field]: key });
    }
  };

  return (
    <FormInput visible={visible} id={id}>
      <Input label={label} id={field} required={required} tooltip={tooltip} errors={errors} hasError={showError} warnings={warnings}>
        <RadioListInputWrapper css={containerStyle}>
          {
            Object.entries(options)?.map(([key, option]) => (
              <RadioInput
                key={option}
                id={`${id || field}-${key}-radio`}
                name={field}
                label={labelOverride?.[key] ?? option}
                value={value === key}
                touchOverride={touchOverride}
                errors={errors}
                onChange={(newValue: boolean) => (localUpdate(key, newValue))}
                isInline={isInline}
                disabled={(typeof disabled === 'boolean' ? disabled : disabled?.[key]) || isDisabled}
                onBlur={() => setTouched(true)}
              />
            ))
          }
        </RadioListInputWrapper>
      </Input>
    </FormInput>
  );
}
